import { useRef, useState } from 'react';
import type { AiChartPart } from 'juneau';
import { CHART_DATE_RANGE_SEPARATOR, ChartSeriesFormat, isChartTextKey } from '@insio/shared-types';
import { ChartCard, type ChartCardExportFormat } from '@insio/ui-toolkit';
import { ChartExportModal } from './ChartExportModal';
import { useChartExport } from './use-chart-export';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { isPeriodLabelFormat, useFormatPeriodLabel } from '../use-format-period-label';
import { useFormatReportPeriod } from '../use-format-report-period';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useChartLabel } from './use-chart-label';

/**
 * Chart part as the backend emits it — juneau's schema does not yet declare the
 * optional label hints, so they are described here.
 */
interface ChartPartData extends AiChartPart {
  /** i18n namespace translating the point x-values (e.g. status keys). */
  labelSet?: string;
  /** i18n namespace translating the series names. */
  seriesLabelSet?: string;
  /** "dateRange" when series are named by ISO date ranges ("2024-01-01 – 2024-01-31"). */
  seriesFormat?: string;
  /** Period bucket format of the x-values ("month", "quarter", "year", "date"). */
  xFormat?: string;
}

interface Props {
  part: ChartPartData;
}

/** Renders a juneau chart part with the Tappeer ChartCard and locale-aware amounts. */
export function ChartPart({ part }: Props) {
  const formatCurrency = useFormatCurrency();
  const formatPeriodLabel = useFormatPeriodLabel();
  const formatReportPeriod = useFormatReportPeriod();
  const chartLabel = useChartLabel();
  const t = useFeatureTranslation('aiChat');
  const { exporting, hasError, exportChart } = useChartExport();
  const [exportModalOpen, setExportModalOpen] = useState(false);
  // Capture thunk from ChartCard — stored so the modal can use it after opening.
  const capturePngRef = useRef<(() => Promise<string>) | null>(null);

  const formatValue = (value: number, currency?: string): string =>
    formatCurrency(value, currency ?? null) ?? value.toLocaleString();

  const { labelSet, seriesLabelSet, seriesFormat, xFormat } = part;

  // Period buckets are formatted; everything else goes through the label translator,
  // which leaves display text (supplier names) untouched.
  const pointLabel = (x: string): string =>
    labelSet === undefined && isPeriodLabelFormat(xFormat) && !isChartTextKey(x)
      ? formatPeriodLabel(x, xFormat)
      : chartLabel(x, labelSet);

  const seriesName = (label: string): string => {
    if (seriesFormat === ChartSeriesFormat.DATE_RANGE) {
      const [from, to] = label.split(CHART_DATE_RANGE_SEPARATOR);
      if (from && to) return formatReportPeriod(from, to);
    }
    return chartLabel(label, seriesLabelSet);
  };

  const series = part.series.map(s => ({
    ...s,
    label: seriesName(s.label),
    points: s.points.map(point => ({ ...point, x: pointLabel(point.x) }))
  }));

  const handleExportClick = (capturePng: () => Promise<string>): void => {
    capturePngRef.current = capturePng;
    setExportModalOpen(true);
  };

  const handleExport = (format: ChartCardExportFormat): Promise<boolean> => {
    const capturePng = capturePngRef.current;
    if (!capturePng) return Promise.resolve(false);
    return exportChart({
      format,
      title: part.title,
      kind: part.kind,
      yFormat: part.yFormat,
      series,
      capturePng,
      labelHeader: t('chartExport.labelHeader')
    });
  };

  return (
    <>
      <ChartCard
        kind={part.kind}
        title={part.title}
        series={series}
        yFormat={part.yFormat}
        formatValue={formatValue}
        onExportClick={handleExportClick}
        exportLabel={t('chartExport.button')}
        testId='ai-chat-chart'
      />
      <ChartExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        chartTitle={part.title}
        exporting={exporting}
        hasError={hasError}
        onExport={handleExport}
      />
    </>
  );
}
